import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { apiService, submitFeedback } from '../../services/api';
import { getPartner } from '../../services/partnerService';
import { updateTenderInsights } from '../../services/tenderAIService';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { type Tender, TenderStatus } from '../../types/tender';

import ArchiveTenderDialog, { type ArchiveTenderParams } from '../Dialogs/ArchiveTenderDialog';
import FeedbackDialog from './FeedbackDialog';
import {
  type FeedbackSubmission,
  generateICSData,
  getDeadlineDate,
  parseSubmissionDeadline,
} from './TenderTable/helpers';
import TenderCard from './TenderTable/TenderCard';
import TenderTableEmptyState from './TenderTable/TenderTableEmptyState';
import TenderTableFilters from './TenderTable/TenderTableFilters';

interface TenderTableProps {
  rows: Tender[];
  isLoading: boolean;
  error: string | null;
  onFetchTenders: () => Promise<void>;
  onToggleReadStatus: (tenderId: string, read: boolean) => void;
  onArchiveTender: (tenderId: string, reason: string) => Promise<void>;
  onUploadNewDraftForRow: (tenderId: string, files: File[]) => void;
  showDropzoneSelectButton?: boolean;
  usersMap?: Record<string, { displayName: string; teamId?: string }>;
  teamsMap?: Record<string, string>;
  onViewChange?: (tenders: Tender[]) => void;
}

type SortableValue = string | number | Date | boolean | null | undefined;

const TenderTable: React.FC<TenderTableProps> = ({
  rows: tenders,
  isLoading: isLoadingTenders,
  error: tendersError,
  onFetchTenders,
  onToggleReadStatus: _onToggleReadStatus,
  onArchiveTender,
  onUploadNewDraftForRow: _onUploadNewDraftForRow,
  showDropzoneSelectButton: _showDropzoneSelectButton = true,
  usersMap,
  teamsMap,
  onViewChange,
}) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const { account, hasPermission } = useAccount();

  const canViewAll = hasPermission(PERMISSIONS.TENDER_VIEW_CLIENT);
  const canManageTeam = hasPermission(PERMISSIONS.TENDER_MANAGE_TEAM);
  const allLabel = !canViewAll && canManageTeam ? 'Team Tenders' : 'All Tenders';

  const [filteredRows, setFilteredRows] = useState<Tender[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState({ key: 'submissionDeadline', direction: 'asc' });
  const [filterOwner, setFilterOwner] = useState<'all' | 'mine'>('all');
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<string>('all');

  const [tenderToAction, setTenderToAction] = useState<Tender | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedTenderForFeedback, setSelectedTenderForFeedback] = useState<Tender | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const relevantUserIds = useMemo(() => {
    const ids = new Set<string>();
    if (tenders) {
      for (const t of tenders) {
        if (t.ownerId) {
          ids.add(t.ownerId);
        }
      }
    }
    return ids;
  }, [tenders]);

  const relevantTeamIds = useMemo(() => {
    const ids = new Set<string>();
    if (tenders && usersMap) {
      for (const t of tenders) {
        const owner = usersMap[t.ownerId || ''];
        if (owner?.teamId) {
          ids.add(owner.teamId);
        }
      }
    }
    return ids;
  }, [tenders, usersMap]);

  const [calendarAnchorEl, setCalendarAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTenderForCalendar, setSelectedTenderForCalendar] = useState<Tender | null>(null);

  const [partnersMap, setPartnersMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!account?.clientId) {
      return;
    }

    const partnerClientIds = new Set<string>();
    if (tenders) {
      for (const t of tenders) {
        if (t.clientId && t.clientId !== account.clientId) {
          partnerClientIds.add(t.clientId);
        }
      }
    }

    if (partnerClientIds.size === 0) {
      return;
    }

    const fetchPartners = async () => {
      try {
        const newMap: Record<string, string> = {};
        for (const pid of partnerClientIds) {
          if (!partnersMap[pid]) {
            const partnerData = await getPartner(pid);
            if (partnerData?.companyName) {
              newMap[pid] = String(partnerData.companyName);
            } else {
              newMap[pid] = 'External Company';
            }
          }
        }
        if (Object.keys(newMap).length > 0) {
          setPartnersMap((prev) => ({ ...prev, ...newMap }));
        }
      } catch (_err) {
        // ignore
      }
    };
    fetchPartners();
  }, [tenders, account?.clientId, partnersMap]);

  const handleCalendarClick = (event: React.MouseEvent<HTMLButtonElement>, tender: Tender) => {
    setCalendarAnchorEl(event.currentTarget);
    setSelectedTenderForCalendar(tender);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
    setSelectedTenderForCalendar(null);
  };

  const primaryButtonStyle = {
    backgroundColor: theme.palette.bidlogiq?.brick || theme.palette.primary.dark,
    color: `${theme.palette.common.white} !important`,
    fontWeight: 'bold',
    fontSize: '1.0rem',
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    border: 'none',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.bidlogiq?.brick || theme.palette.primary.dark,
      boxShadow: theme.shadows[4],
      color: `${theme.palette.primary.contrastText} !important`,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  };

  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const getLoadingKey = (tenderId: string, action: string) => `${tenderId}_${action}`;

  const handleDownloadFileWithLoading = async (
    gsURI: string,
    fileTypeIdentifier: string,
    defaultFileName: string,
    tenderId: string
  ) => {
    if (!gsURI) {
      showSnackbar('File path is missing, cannot download.', 'error', 2000);
      return;
    }
    setLoading((prev) => ({ ...prev, [getLoadingKey(tenderId, fileTypeIdentifier)]: true }));
    try {
      const url = await apiService.getTenderFileDownloadUrl(gsURI);
      if (url) {
        if (gsURI.startsWith('gs://')) {
          // Check if the tender itself is encrypted (via tenders state in TenderTable, though we only have tenderId here)
          // Wait, we have the `tenders` array, so we can look up the tender object.
          const tenderObj = tenders?.find((t) => t.id === tenderId);
          if (tenderObj?.encryptionMode === 'envelope') {
            showSnackbar(
              'Cannot download: You lack the encryption key to decrypt this file.',
              'error'
            );
            return;
          }
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            defaultFileName || gsURI.substring(gsURI.lastIndexOf('/') + 1)
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            defaultFileName || gsURI.substring(gsURI.lastIndexOf('/') + 1)
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        showSnackbar(
          `Successfully started download for ${defaultFileName || fileTypeIdentifier}.`,
          'success',
          2000
        );
      } else {
        showSnackbar('Could not get download URL.', 'error', 2000);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      showSnackbar(`Failed to start download: ${errorMessage}`, 'error', 2000);
    } finally {
      setLoading((prev) => ({ ...prev, [getLoadingKey(tenderId, fileTypeIdentifier)]: false }));
    }
  };

  useEffect(() => {
    const currentRowsData = Array.isArray(tenders) ? tenders : [];
    let newFilteredRows = [...currentRowsData];

    if (filterOwner === 'mine' && account?.uid) {
      newFilteredRows = newFilteredRows.filter((row) => row.ownerId === account.uid);
    } else if (filterOwner === 'all') {
      if (selectedTeam !== 'all') {
        newFilteredRows = newFilteredRows.filter((row) => {
          const ownerTeamId = usersMap?.[row.ownerId || '']?.teamId;
          return ownerTeamId === selectedTeam;
        });
      }
      if (selectedUser !== 'all') {
        newFilteredRows = newFilteredRows.filter((row) => row.ownerId === selectedUser);
      }
    }

    if (stateFilter.length > 0) {
      newFilteredRows = newFilteredRows.filter((tender) => {
        const tState = (tender.state || tender.location || '').toUpperCase();
        return stateFilter.some((s) => tState.includes(s)) || tState.includes('NATIONAL');
      });
    }

    if (searchTerm) {
      newFilteredRows = newFilteredRows.filter(
        (row) =>
          row.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.tenderIssuer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (row.keywords &&
            Array.isArray(row.keywords) &&
            row.keywords.some((kw: string) => kw.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    if (sortConfig.key) {
      newFilteredRows.sort((a, b) => {
        let aValue: SortableValue, bValue: SortableValue;
        if (sortConfig.key === 'submissionDeadline') {
          const dateA = parseSubmissionDeadline(a.submissionDeadline);
          const dateB = parseSubmissionDeadline(b.submissionDeadline);
          aValue = dateA ? dateA.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
          bValue = dateB ? dateB.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
        } else if (sortConfig.key === 'title') {
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
        } else if (sortConfig.key === 'client') {
          aValue = (a.tenderIssuer || '').toLowerCase();
          bValue = (b.tenderIssuer || '').toLowerCase();
        } else {
          aValue = a[sortConfig.key as keyof Tender] as string | number;
          bValue = b[sortConfig.key as keyof Tender] as string | number;
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          } else if (aValue === null || aValue === undefined) {
            aValue = sortConfig.direction === 'asc' ? Infinity : -Infinity;
          } else if (bValue === null || bValue === undefined) {
            bValue = sortConfig.direction === 'asc' ? Infinity : -Infinity;
          }
        }
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredRows(newFilteredRows);
    if (onViewChange) {
      onViewChange(newFilteredRows);
    }
  }, [
    searchTerm,
    stateFilter,
    tenders,
    sortConfig,
    filterOwner,
    selectedTeam,
    selectedUser,
    account?.uid,
    usersMap,
    onViewChange,
  ]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    const [key, direction] = value.split('-');
    if (key && direction) {
      setSortConfig({ key, direction });
    }
  };

  const handleTeamChange = (event: SelectChangeEvent) => {
    setSelectedTeam(event.target.value);
    setSelectedUser('all');
  };

  const handleUserChange = (event: SelectChangeEvent) => {
    setSelectedUser(event.target.value);
  };

  const handleFilterOwnerChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: 'all' | 'mine' | null
  ) => {
    if (newFilter !== null) {
      setFilterOwner(newFilter);
      if (newFilter === 'mine') {
        setSelectedTeam('all');
        setSelectedUser('all');
      }
    }
  };

  const handleFilterOwnerSelectChange = (e: SelectChangeEvent) => {
    const val = e.target.value;
    if (val === 'mine' || val === 'all') {
      setFilterOwner(val);
      if (val === 'mine') {
        setSelectedTeam('all');
        setSelectedUser('all');
      }
    }
  };

  const openArchiveDialog = (tender: Tender) => {
    setTenderToAction(tender);
    setArchiveDialogOpen(true);
  };

  const handleCloseGenericDialog = () => {
    setTenderToAction(null);
    setArchiveDialogOpen(false);
  };

  const handleArchiveTender = async (params: ArchiveTenderParams) => {
    if (!tenderToAction) {
      return;
    }
    setIsArchiving(true);
    try {
      if (
        params.tenderValue !== undefined ||
        params.bidCost !== undefined ||
        params.includeInMetrics !== undefined
      ) {
        await updateTenderInsights(tenderToAction.id || '', {
          tenderValue: params.tenderValue ?? null,
          bidCost: params.bidCost ?? null,
          includeInMetrics: params.includeInMetrics,
        });
      }
      await onArchiveTender(tenderToAction.id || '', params.reason);
      showSnackbar(`Tender "${tenderToAction.title}" archived successfully.`, 'success');
      setSelectedTenderForFeedback(tenderToAction);
      setFeedbackDialogOpen(true);
      handleCloseGenericDialog();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      showSnackbar(`Error archiving tender: ${errorMessage}`, 'error');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleArchiveClick = (tender: Tender) => {
    openArchiveDialog(tender);
  };

  const handleOpenFeedbackDialog = (tender: Tender) => {
    setSelectedTenderForFeedback(tender);
    setFeedbackDialogOpen(true);
  };

  const handleSubmitFeedback = async (feedback: FeedbackSubmission) => {
    if (!selectedTenderForFeedback) {
      return;
    }
    setIsSubmittingFeedback(true);
    try {
      await submitFeedback(selectedTenderForFeedback.id || '', JSON.stringify(feedback));
      showSnackbar('Thank you for your feedback!', 'success');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      showSnackbar(`Error submitting feedback: ${errorMessage}`, 'error');
    } finally {
      setIsSubmittingFeedback(false);
      setFeedbackDialogOpen(false);
    }
  };

  const handleOutlookICALReminderTable = (tender: Tender) => {
    const deadline = getDeadlineDate(tender);
    if (deadline) {
      const icsData = generateICSData(tender, deadline);
      if (icsData) {
        const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const safeTitle = (tender.title || 'Tender')
          .replace(/[^a-z0-9_\s-]/gi, '')
          .replace(/\s+/g, '_');
        link.download = `${safeTitle}_Outlook_Reminder.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } else {
        showSnackbar(
          'Could not generate Outlook/iCal reminder. ICS data creation failed.',
          'error'
        );
      }
    } else {
      showSnackbar(
        'Could not generate Outlook/iCal reminder. Tender data might be incomplete, missing a valid deadline, or the deadline is in an unparsable format.',
        'error'
      );
    }
  };

  const handleGoogleCalendarLinkTable = (tender: Tender) => {
    const deadline = getDeadlineDate(tender);
    if (deadline) {
      const toGoogleDateTime = (date: Date) => date.toISOString().replace(/-|:|\.\d{3}/g, '');
      const eventStart = deadline;
      const eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
      const title = `Tender Closing: ${tender.title || 'N/A'}`;
      let description = `Tender: ${tender.title || 'N/A'}\nTender ID: ${tender.tenderId || tender.opportunityID || tender.id || 'N/A'}\nClient: ${tender.tenderIssuer || 'N/A'}`;
      if (tender.summary) {
        description += `\n\nSummary: ${tender.summary}`;
      }
      description += '\n\nACTION: Please set a 1-week reminder for this tender deadline.';
      const googleBaseUrl = 'https://www.google.com/calendar/render?action=TEMPLATE';
      const params = new URLSearchParams({
        text: title,
        dates: `${toGoogleDateTime(eventStart)}/${toGoogleDateTime(eventEnd)}`,
        details: description,
      });
      window.open(`${googleBaseUrl}&${params.toString()}`, '_blank', 'noopener,noreferrer');
    } else {
      showSnackbar(
        'Could not generate Google Calendar link. Tender data might be incomplete, missing a valid deadline, or the deadline is in an unparsable format.',
        'error'
      );
    }
  };

  const isFileProcessing = (
    tender: Tender | null,
    docType: 'analysis' | 'response' | 'review' | 'compile'
  ) => {
    if (!tender) {
      return false;
    }
    if (tender.status === TenderStatus.CONVERSION_ERROR && docType === 'review') {
      return false;
    }

    const isFinalState = (s: string) =>
      s && (s.includes('complete') || s.includes('error') || s.includes('failed'));
    let subStatus = '';
    let keywords: string[] = [];
    const mainStatus = (tender.status || '').toLowerCase();

    if (docType === 'analysis') {
      subStatus = (tender.alignmentAnalysis?.status || '').toLowerCase();
      keywords = ['analysis', 'report'];
    } else if (docType === 'response') {
      subStatus = (tender.draftResponse?.status || '').toLowerCase();
      keywords = ['response', 'draft'];
    } else if (docType === 'review') {
      subStatus = (tender.reviewResponse?.status || '').toLowerCase();
      keywords = ['review'];
    } else if (docType === 'compile') {
      subStatus = (tender.compileStatus?.status || '').toLowerCase();
      keywords = ['compiling', 'compile'];
    }

    const updatedTender = filteredRows.find((row) => row.id === tender.id);
    if (updatedTender) {
      let updatedSubStatus = '';
      if (docType === 'analysis') {
        updatedSubStatus = (updatedTender.alignmentAnalysis?.status || '').toLowerCase();
      } else if (docType === 'response') {
        updatedSubStatus = (updatedTender.draftResponse?.status || '').toLowerCase();
      } else if (docType === 'review') {
        updatedSubStatus = (updatedTender.reviewResponse?.status || '').toLowerCase();
      } else if (docType === 'compile') {
        updatedSubStatus = (updatedTender.compileStatus?.status || '').toLowerCase();
      }

      if (updatedSubStatus && !isFinalState(updatedSubStatus)) {
        return true;
      }
    }

    if (subStatus && !isFinalState(subStatus)) {
      return true;
    }
    if (isFinalState(subStatus)) {
      return false;
    }

    return !isFinalState(mainStatus) && keywords.some((k) => mainStatus.includes(k));
  };

  const handleStepperDownload = (
    tender: Tender,
    type: 'alignment' | 'response' | 'review' | 'compile'
  ) => {
    let link = '';
    let fileName = '';
    if (type === 'alignment') {
      link = tender.analysisFileLink || '';
      fileName = tender.title ? `${tender.title} - Alignment.docx` : 'alignment.docx';
    } else if (type === 'response') {
      link = tender.draftFileLink || '';
      fileName = tender.title ? `${tender.title} - Response.docx` : 'response.docx';
    } else if (type === 'review') {
      link = tender.reviewFileLink || '';
      fileName = tender.title ? `${tender.title} - Review.docx` : 'review.docx';
    } else if (type === 'compile') {
      link = tender.compiledFileLink || '';
      fileName = tender.title ? `${tender.title} - Proposal.docx` : 'proposal.docx';
    }

    if (link) {
      handleDownloadFileWithLoading(link, type, fileName, tender.id || '');
    }
  };

  const isLoadingFile = (tenderId: string, type: string) =>
    !!loading[getLoadingKey(tenderId, type)];

  if (isLoadingTenders && !tenders?.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress
          sx={{ color: theme.palette.bidlogiq?.orange || theme.palette.primary.main }}
        />
        <Typography ml={2}>Loading tenders...</Typography>
      </Box>
    );
  }

  if (tendersError) {
    return (
      <Box textAlign="center" p={3} sx={{ color: theme.palette.error.main }}>
        <Typography color="inherit">Error loading tenders: {tendersError}</Typography>
        <Button variant="contained" onClick={onFetchTenders} sx={{ mt: 2, ...primaryButtonStyle }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <TenderTableFilters
        filterOwner={filterOwner}
        onFilterOwnerChange={handleFilterOwnerChange}
        onFilterOwnerSelectChange={handleFilterOwnerSelectChange}
        allLabel={allLabel}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        sortConfig={sortConfig}
        onSortChange={handleSortChange}
        stateFilter={stateFilter}
        onStateFilterChange={(e) =>
          setStateFilter(
            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
          )
        }
        teamsMap={teamsMap}
        relevantTeamIds={relevantTeamIds}
        selectedTeam={selectedTeam}
        onTeamChange={handleTeamChange}
        usersMap={usersMap}
        relevantUserIds={relevantUserIds}
        selectedUser={selectedUser}
        onUserChange={handleUserChange}
      />

      {isLoadingTenders && filteredRows.length > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1300,
            backgroundColor: theme.palette.primary.dark,
            padding: 2,
            borderRadius: 2,
            boxShadow: 5,
          }}
        >
          <CircularProgress />
          <Typography ml={2}>Updating public tenders...</Typography>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        {filteredRows.length > 0 ? (
          filteredRows.map((tender) => (
            <TenderCard
              key={tender.id}
              tender={tender}
              account={account}
              partnersMap={partnersMap}
              usersMap={usersMap}
              teamsMap={teamsMap}
              onCalendarClick={handleCalendarClick}
              onStepperDownload={handleStepperDownload}
              isFileProcessing={isFileProcessing}
              isLoadingFile={isLoadingFile}
              onOpenFeedback={handleOpenFeedbackDialog}
              onArchiveClick={handleArchiveClick}
              primaryButtonStyle={primaryButtonStyle}
            />
          ))
        ) : (
          <TenderTableEmptyState
            isLoading={isLoadingTenders}
            hasTenders={tenders.length > 0}
            primaryButtonStyle={primaryButtonStyle}
          />
        )}
      </Box>

      <Menu
        anchorEl={calendarAnchorEl}
        open={Boolean(calendarAnchorEl)}
        onClose={handleCalendarClose}
      >
        <MenuItem
          onClick={() =>
            selectedTenderForCalendar && handleOutlookICALReminderTable(selectedTenderForCalendar)
          }
        >
          Outlook / iCal
        </MenuItem>
        <MenuItem
          onClick={() =>
            selectedTenderForCalendar && handleGoogleCalendarLinkTable(selectedTenderForCalendar)
          }
        >
          Google Calendar
        </MenuItem>
      </Menu>

      <ArchiveTenderDialog
        open={archiveDialogOpen}
        onClose={handleCloseGenericDialog}
        tenderTitle={tenderToAction?.title}
        tenderId={tenderToAction?.id}
        initialValue={tenderToAction?.tenderValue}
        initialCost={tenderToAction?.bidCost}
        initialInclude={tenderToAction?.includeInMetrics}
        onConfirm={handleArchiveTender}
        isLoading={isArchiving}
      />

      {selectedTenderForFeedback && (
        <FeedbackDialog
          open={feedbackDialogOpen}
          onClose={() => setFeedbackDialogOpen(false)}
          tender={selectedTenderForFeedback}
          onSubmit={handleSubmitFeedback}
          isLoading={isSubmittingFeedback}
        />
      )}
    </Box>
  );
};

export default TenderTable;
